<script setup>
import { ref, computed, h, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from 'dashboard/components-next/filter/operators';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { WORKFLOWS } from './constants';

const props = defineProps({
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
});

const conditions = defineModel('conditions', {
  type: Array,
  default: () => [],
});

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const { t } = useI18n();
const { operators } = useOperators();

const dialogRef = ref(null);
const conditionsRef = useTemplateRef('conditionsRef');
const errors = ref({});

const filterTypes = computed(() => {
  const attributes = WORKFLOWS.auto_resolve_conditions.conditions;

  return attributes.map(attr => {
    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;

      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: t(`AUTOMATION.ATTRIBUTES.${attr.name}`),
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: 'standard',
    };
  });
});
const hasConditionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('condition_'))
);

const resetValidation = () => {
  errors.value = {};
  conditionsRef.value?.forEach(c => c.resetValidation());
};

const open = () => {
  resetValidation();
  dialogRef.value?.open();
};

const close = () => {
  resetValidation();
  dialogRef.value?.close();
};

defineExpose({ open, close });
</script>

<template>
  <section class="mb-5">
    <label>
      {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
    </label>
    <ul
      class="grid gap-4 list-none p-3 mb-4 outline outline-1 rounded-xl -outline-offset-1"
      :class="
        hasConditionErrors
          ? 'outline-n-ruby-5 bg-n-ruby-2/50'
          : 'outline-n-weak dark:outline-n-strong'
      "
    >
      <template v-for="(condition, i) in conditions" :key="i">
        <ConditionRow
          v-if="i === 0"
          ref="conditionsRef"
          v-model:attribute-key="conditions[i].attribute_key"
          v-model:filter-operator="conditions[i].filter_operator"
          v-model:values="conditions[i].values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(i)"
        />
        <ConditionRow
          v-else
          ref="conditionsRef"
          v-model:attribute-key="conditions[i].attribute_key"
          v-model:filter-operator="conditions[i].filter_operator"
          v-model:query-operator="conditions[i - 1].query_operator"
          v-model:values="conditions[i].values"
          :filter-types="filterTypes"
          show-query-operator
          @remove="removeFilter(i)"
        />
      </template>
      <div>
        <NextButton
          icon="i-lucide-plus"
          blue
          faded
          sm
          :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
          @click="appendNewCondition"
        />
      </div>
    </ul>
  </section>
</template>
